import { useEventListener } from 'usehooks-ts';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Persisted open/closed state so the global widget keeps it across route changes (a URL param would
// get dropped on navigation) AND across a full reload.
type ChatWidgetOpenState = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
};

const useStore = create<ChatWidgetOpenState>()(
  persist(
    set => ({
      isOpen: false,
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      toggle: () => set(state => ({ isOpen: !state.isOpen })),
    }),
    { name: 'percy.chat-widget-open', partialize: state => ({ isOpen: state.isOpen }) },
  ),
);

/** Open/closed state for the global floating chat widget. */
const useChatWidgetOpen = () => useStore();

/**
 * Registers the Cmd/Ctrl+Shift+P toggle. Mount this in exactly ONE place — the shortcut lives here
 * (not in useChatWidgetOpen) so multiple consumers of the open state don't each add a listener and
 * double-toggle on a single keypress.
 */
export const useChatWidgetShortcut = () => {
  useEventListener('keydown', e => {
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'p') {
      e.preventDefault();
      useStore.getState().toggle();
    }
  });
};

export default useChatWidgetOpen;
